<script setup lang="ts">
import { pTooltipContentCva, type PTooltipContentVariants } from '@ui/components/core/p-tooltip/index.js';
import { type HTMLAttributes } from 'vue';
import { cn } from '@ui/lib/utils.js';
import { reactiveOmit } from '@vueuse/core';
import {
    TooltipArrow,
    TooltipContent,
    type TooltipContentEmits,
    type TooltipContentProps as ContentProps,
    TooltipPortal,
    useForwardPropsEmits,
} from 'reka-ui';

interface TooltipContentProps extends Partial<ContentProps> {
    class?: HTMLAttributes['class'];
    variant?: PTooltipContentVariants['variant'];
    hideArrow?: boolean;
}

defineOptions({
    inheritAttrs: false,
});

const props = withDefaults(defineProps<TooltipContentProps>(), {
    sideOffset: 4,
    variant: 'default',
    hideArrow: false,
});

const emits = defineEmits<TooltipContentEmits>();

const delegatedProps = reactiveOmit(props, 'class');
const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <TooltipPortal>
        <TooltipContent
            data-slot="tooltip-content"
            v-bind="{ ...forwarded, ...$attrs }"
            :class="cn(pTooltipContentCva({ variant: props.variant }), props.class)"
        >
            <slot />

            <TooltipArrow
                v-if="!props.hideArrow && props.variant === 'default'"
                :class="
                    cn(
                        'bg-popover-popover',
                        'fill-popover-popover',
                        'z-50',
                        'size-2.5',
                        'translate-y-[calc(-50%-2px)]',
                        'rotate-45',
                        'rounded-[2px]',
                    )
                "
            />
        </TooltipContent>
    </TooltipPortal>
</template>
